<template>
    <Card>
        <QuickpaySettingsPaymentPartnersLoader v-if="isLoading" />
        <template v-else>
            <Heading
                :labelTitle="translations.title"
                variant="h2"
                :prefixColoredIcon="{ icon: 'supercondensed-fill', color: 'brand' }"
            />
            <p class="quickpay-payment-partners__description typographies-paragraph-medium-regular">
                {{ translations.description }}
            </p>
            <Transition name="recommendation-dismiss" @after-leave="dismissRecommendation">
                <QuickpaySettingsRecommendation
                    v-if="showRecommendation && recommendedProvider"
                    :provider="recommendedProvider"
                    @dismiss="isDismissing = true"
                    @connect="handleConnect"
                />
            </Transition>
            <div class="quickpay-payment-partners__list">
                <QuickpaySettingsProviderCard
                    v-for="provider in alwaysVisibleProviders"
                    :key="provider.id"
                    :provider="provider"
                    @connect="handleConnect"
                    @configure="handleConfigure"
                />
            </div>
            <div
                class="quickpay-payment-partners__expandable"
                :class="{ 'quickpay-payment-partners__expandable--open': showAll }"
            >
                <div class="quickpay-payment-partners__expandable-content">
                    <div
                        class="quickpay-payment-partners__list quickpay-payment-partners__list--extra"
                    >
                        <QuickpaySettingsProviderCard
                            v-for="provider in extraProviders"
                            :key="provider.id"
                            :provider="provider"
                            @connect="handleConnect"
                            @configure="handleConfigure"
                        />
                    </div>
                </div>
            </div>
            <CompassPlainButton
                v-if="sortedProviders.length > DEFAULT_VISIBLE_COUNT"
                :label="toggleLabel"
                class="quickpay-payment-partners__toggle"
                @click="showAll = !showAll"
            />
        </template>
    </Card>
    <QuickpaySettingsSetupProviderModal
        v-if="selectedProvider"
        v-model:visible="showSetupModal"
        :provider="selectedProvider"
        :isLoading="isConnecting"
        @connectProvider="handleConnectProvider"
    />
</template>

<script setup>
import { ref, computed, inject } from 'vue';
import { useStore } from 'vuex';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import QuickpaySettingsPaymentPartnersLoader from 'vcomponents/quickpay/settings/QuickpaySettingsPaymentPartnersLoader.vue';
import QuickpaySettingsProviderCard from 'vcomponents/quickpay/settings/QuickpaySettingsProviderCard.vue';
import QuickpaySettingsRecommendation from 'vcomponents/quickpay/settings/QuickpaySettingsRecommendation.vue';
import QuickpaySettingsSetupProviderModal from 'vcomponents/quickpay/settings/QuickpaySettingsSetupProviderModal.vue';
import { usePaymentProviders } from 'composables/usePaymentProviders';
import { enablePlugin } from 'api/quickpay/quickpayService';

const DEFAULT_VISIBLE_COUNT = 3;
const RECOMMENDATION_STORAGE_KEY = 'quickpay_recommendation_dismissed';

const props = defineProps({
    providerStatuses: {
        type: Object,
        default: () => ({}),
    },
    isLoading: {
        type: Boolean,
        default: false,
    },
});

const $t = inject('$t');
const $pushToast = inject('$pushToast');
const store = useStore();

const translations = {
    title: $t.__('quickpay.settings.paymentPartners.title'),
    description: $t.__('quickpay.settings.paymentPartners.description'),
    showMore: $t.__('quickpay.settings.paymentPartners.showMore'),
    showLess: $t.__('quickpay.settings.paymentPartners.showLess'),
};

const { sortedProviders } = usePaymentProviders(() => props.providerStatuses, $t);

const showAll = ref(false);
const selectedProvider = ref(null);
const showSetupModal = ref(false);
const isConnecting = ref(false);
const getDismissedProviders = () => {
    try {
        const stored = localStorage.getItem(RECOMMENDATION_STORAGE_KEY);
        if (stored === 'true') {
            return [];
        }
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const dismissedProviders = ref(getDismissedProviders());

const isDismissing = ref(false);

const recommendedProvider = computed(() => {
    return (
        sortedProviders.value.find(
            (provider) =>
                provider.recommendation &&
                !provider.isConnected &&
                !dismissedProviders.value.includes(provider.id),
        ) ?? null
    );
});

const showRecommendation = computed(
    () => recommendedProvider.value !== null && !isDismissing.value,
);

const alwaysVisibleProviders = computed(() => {
    return sortedProviders.value.slice(0, DEFAULT_VISIBLE_COUNT);
});

const extraProviders = computed(() => {
    return sortedProviders.value.slice(DEFAULT_VISIBLE_COUNT);
});

const toggleLabel = computed(() => {
    return showAll.value ? translations.showLess : translations.showMore;
});

const dismissRecommendation = () => {
    const provider = recommendedProvider.value;
    if (provider) {
        dismissedProviders.value = [...dismissedProviders.value, provider.id];
        localStorage.setItem(RECOMMENDATION_STORAGE_KEY, JSON.stringify(dismissedProviders.value));
    }

    isDismissing.value = false;
};

const handleConnect = (providerId) => {
    selectedProvider.value =
        sortedProviders.value.find((provider) => provider.id === providerId) ?? null;
    showSetupModal.value = true;
};

const configureActions = {
    redirect: (configAction) => {
        window.location.href = configAction.url;
    },
    legacyPopup: (configAction) => {
        if (configAction.legacyId === 'paypal') {
            window.ExtPaypal?.loadPrefs();
        }
    },
    vuePopup: (configAction) => {
        store.dispatch('popupPanel/open', {
            key: configAction.popupKey,
            props: {},
        });
    },
};

const handleConfigure = (providerId) => {
    const provider = sortedProviders.value.find((provider) => provider.id === providerId);
    if (!provider?.configAction) {
        return;
    }

    const handler = configureActions[provider.configAction.type];
    if (handler) {
        handler(provider.configAction);
    }
};

const handleConnectProvider = async () => {
    const provider = selectedProvider.value;
    if (!provider?.configAction) {
        return;
    }

    if (provider.status === 'disabled') {
        isConnecting.value = true;
        try {
            await enablePlugin(provider.pluginName);
        } catch {
            $pushToast({
                type: 'error',
                icon: true,
                text: $t.__('common.labels.toast.error'),
            });
            showSetupModal.value = false;
            return;
        } finally {
            isConnecting.value = false;
        }
    }

    showSetupModal.value = false;

    const handler = configureActions[provider.configAction.type];
    if (handler) {
        handler(provider.configAction);
    }
};
</script>

<style lang="scss" scoped>
.quickpay-payment-partners {
    &__description {
        margin: 0 0 var(--spacing-3);
        color: var(--colors-common-text-standard-default);
    }
    &__list {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--spacing-3);
        padding-bottom: var(--spacing-3);
    }
    &__expandable {
        display: grid;
        grid-template-rows: 0fr;
        overflow: hidden;
        transition: grid-template-rows 0.3s ease 0.1s;
        &--open {
            grid-template-rows: 1fr;
            overflow: visible;
            transition: grid-template-rows 0.3s ease;
        }
    }
    &__expandable-content {
        min-height: 0;
        opacity: 0;
        transition: opacity 0.2s ease;

        .quickpay-payment-partners__expandable--open & {
            opacity: 1;
            transition: opacity 0.2s ease 0.15s;
        }
    }
    &__toggle {
        display: block;
        margin: 0 auto;
    }
}

.recommendation-dismiss-leave-active {
    transition:
        opacity 0.25s ease,
        margin-bottom 0.3s ease 0.1s,
        max-height 0.3s ease 0.1s;
    max-height: 300px;
    overflow: hidden;
}

.recommendation-dismiss-leave-to {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
}
</style>
